'use client';

import * as React from 'react';
import {usePathname} from 'next/navigation';
import {
  StyledChevron,
  StyledDropdown,
  StyledNavItem,
  StyledNavItemContent,
  StyledSubLink,
  StyledTopLink,
} from '@/components/header/HeaderNavLink.styles';
import {ArrowDownIcon} from '@/components/ui/icons/ArrowDown';
import {LinkWithLink} from '@/components/header/Header';
import {hasLinkAndLabel} from '@/lib/type-guards';

type HeaderNavLinkProps = {
    link: LinkWithLink;
    isSmallScreen?: boolean;
    onClose: () => void;
};

export default function HeaderNavLink({ link, isSmallScreen, onClose }: HeaderNavLinkProps) {
  const pathname = usePathname();
  const navItemRef = React.useRef<HTMLDivElement>(null);
  const isActive =
        pathname === link.link || (link.link !== '/' && pathname !== '/' && Boolean(pathname.includes(link.link!)));
  const subLinks = link.headerSubLinksCollection?.items ?? []
  const hasDropdown = (subLinks && subLinks.length > 0);

  const safeSubLinks = subLinks.filter(hasLinkAndLabel).map(link => ({ ...link, href: link.link! }));

  const handleSubLinkClick = () => {
    // Blur the nav item so :focus-within no longer keeps the dropdown visible
    if (navItemRef.current) {
      navItemRef.current.blur();
      const focused = navItemRef.current.querySelector(':focus') as HTMLElement | null;
      focused?.blur();
    }
    if (isSmallScreen) onClose();
  };

  return (
    <StyledNavItem ref={navItemRef} hasDropdown={hasDropdown} className='header-navitem__dropdown' onClick={() => {
      if (isSmallScreen && !hasDropdown) {
        onClose()
      }
    }}>
      <StyledNavItemContent isActive={isActive}>
        <StyledTopLink
          isSmallScreen={isSmallScreen}
          href={link.link}
          onClick={isSmallScreen && !hasDropdown ? onClose : undefined}
          aria-current={isActive ? 'page' : undefined}
        >
          {link.label}
        </StyledTopLink>

        {hasDropdown && (
          <StyledChevron className='header-navlink__chevron' isSmallScreen={isSmallScreen}>
            <ArrowDownIcon color='currentColor' />
          </StyledChevron>
        )}
      </StyledNavItemContent>

      {hasDropdown && (
        <StyledDropdown className='header-navlink__dropdown' isSmallScreen={isSmallScreen} hasDropdown={hasDropdown}>
          {safeSubLinks.map((subLink) => (
            <StyledSubLink key={subLink.label} href={subLink.href} onClick={handleSubLinkClick} isSmallScreen={isSmallScreen}>
              {subLink.label}
            </StyledSubLink>
          ))}
        </StyledDropdown>
      )}
    </StyledNavItem>
  );
}